/**
 * Background project-action runs.
 *
 * A project action of `kind: "terminal"` and `runMode: "background"` is executed
 * by the server inside the thread's worktree instead of being typed into a
 * terminal, so users without terminal access can still run the project's
 * commands. Progress is pushed on the `workspace.projectScriptStatus` WS
 * channel and drives a toast plus a read-only output modal, the same shape the
 * archive hook uses.
 *
 * One command per run, so there are no steps: `started` is emitted before the
 * process is spawned and exactly one of `completed` / `error` closes the run.
 * A non-zero exit, a timeout and a spawn failure all land in `error`.
 */
import { Schema } from "effect";
import {
  IsoDateTime,
  NonNegativeInt,
  ProjectId,
  ThreadId,
  TrimmedNonEmptyString,
} from "./baseSchemas";

/** Matches the archive hook ceiling: output is truncated server-side. */
const ProjectScriptRunOutputSchema = Schema.String.check(Schema.isMaxLength(1_048_576));

/**
 * Wall-clock budget for one background action. Deliberately the same 2 minutes
 * the archive hook uses: long enough for an install or a build, short enough
 * that a command waiting on stdin cannot hold a slot forever.
 */
export const DEFAULT_PROJECT_SCRIPT_RUN_TIMEOUT_MS = 120_000;

export const ProjectScriptRunPhaseSchema = Schema.Literals(["started", "completed", "error"]);
export type ProjectScriptRunPhase = typeof ProjectScriptRunPhaseSchema.Type;

export const ProjectScriptRunResultSchema = Schema.Struct({
  exitCode: Schema.NullOr(Schema.Int),
  stdout: ProjectScriptRunOutputSchema,
  stderr: ProjectScriptRunOutputSchema,
  timedOut: Schema.Boolean,
  stdoutTruncated: Schema.Boolean,
  stderrTruncated: Schema.Boolean,
  durationMs: NonNegativeInt,
});
export type ProjectScriptRunResult = typeof ProjectScriptRunResultSchema.Type;

export const ProjectScriptRunEventSchema = Schema.Struct({
  runId: TrimmedNonEmptyString.check(Schema.isMaxLength(64)),
  threadId: ThreadId,
  projectId: ProjectId,
  scriptId: TrimmedNonEmptyString,
  scriptName: TrimmedNonEmptyString,
  command: Schema.String.check(Schema.isMaxLength(8_000)),
  cwd: TrimmedNonEmptyString,
  phase: ProjectScriptRunPhaseSchema,
  /** Present on `completed`, and on `error` unless the process never spawned. */
  result: Schema.optional(ProjectScriptRunResultSchema),
  /** Set when the failure has no process behind it (spawn error, timeout note). */
  message: Schema.optional(Schema.String.check(Schema.isMaxLength(2_000))),
  startedAt: IsoDateTime,
  occurredAt: IsoDateTime,
});
export type ProjectScriptRunEvent = typeof ProjectScriptRunEventSchema.Type;
